/**
 * Certificate data client for proba-ui
 * Fetches methodology breakdown (IDs only) and crop yield emissions
 * via platform's external certificate-data API endpoint.
 *
 * Platform stays agnostic; proba-ui resolves methodology names
 * when building the final certificate JSON.
 */

const getPlatformUrl = (): string => {
  return process.env.REACT_APP_PLATFORM_URL || "http://localhost:3333";
};

const getJwtToken = (): string | null => {
  const jwt = localStorage.getItem("jwt");
  if (!jwt) return null;
  // Remove "Bearer " prefix if present
  return jwt.replace(/^Bearer\s+/, "");
};

export type CertificateMetricKey =
  | "Fertilizer emissions (per ha)"
  | "Fertilizer emissions (per ton of crop)"
  | "Fuel emissions (per ton of crop)"
  | "Total emissions";

export interface MethodologyBreakdownItem {
  methodologyId: string;
  assetCount: number;
  totalAssets: number;
  scaleHa: number;
  absoluteReductionPerHa: number;
  totalReduction: number;
}

export interface CertificateMetric {
  metric: CertificateMetricKey;
  baseline: number;
  project: number;
  reduction: number;
}

export interface CropYieldEmissions {
  totalCropYield: number;
  metrics: CertificateMetric[];
}

export interface EmissionReductionOverview {
  baseline: number; // tCO2eq total across included assets
  project: number; // tCO2eq total across included assets
  delta: number; // tCO2eq total reduction across included assets
  reductionPercent: number; // percentage reduction relative to baseline
  emissionIntensity: {
    baseline: number; // tCO2eq / t crop
    project: number; // tCO2eq / t crop
    reduction: number; // tCO2eq / t crop
  };
}

export interface NFRYToken {
  tokenId: string | null;
  issuanceDate: string | null;
  assetId: string;
  externalAssetId: string;
  period: string | null;
  productVolume: number | string | null;
  realizedYield: number | null;
  creditsIncluded: number;
  creditsAvailable: number;
}

export interface ScopeOfCertificate {
  includedAssetIds: string[];
  includedAssetCount: number;
  totalProjectAssetsForCrop: number;
  includedCropCode: string;
  includedCropLabel: string;
  projectCropCodes: string[];
  projectCropLabels: string[];
  isSubsetOfAssets: boolean;
  isSubsetOfCrops: boolean;
  creditsIncluded: number;
  creditsAvailable: number;
  isSubsetOfCredits: boolean;
  nfryTokens: NFRYToken[];
  period: string;
}

export interface EmissionBreakdownByTypeRow {
  component: string;
  baseline: number; // tCO2eq total across included assets
  project: number; // tCO2eq total across included assets
  delta: number; // tCO2eq total reduction across included assets
  deltaPerTon: number; // tCO2eq / t crop
}

export interface EmissionBreakdownByInputRow {
  inputType: string;
  baseline: number; // tCO2eq total across included assets
  project: number; // tCO2eq total across included assets
  delta: number; // tCO2eq total reduction across included assets
  deltaPerTon: number; // tCO2eq / t crop
  /**
   * When true for the "Fertilizers" row, indicates that inhibitor emissions
   * have been included in the fertilizer totals (for display as "+ Inhibitor").
   */
  includesInhibitor?: boolean;
}

export interface EmissionBreakdown {
  byEmissionType: EmissionBreakdownByTypeRow[];
  byInputType: EmissionBreakdownByInputRow[];
}

export interface AssetCropYield {
  assetId: string;
  cropYield: number; // tons
}

export interface CertificateDataResponse {
  projectId: string;
  period: string;
  cropCode: string;
  cropLabel: string;
  methodologyBreakdown: MethodologyBreakdownItem[];
  cropYieldEmissions: CropYieldEmissions;
  assetCropYields: AssetCropYield[]; // Crop yield per asset in tons
  emissionReductionOverview: EmissionReductionOverview;
  emissionBreakdown?: EmissionBreakdown;
}

/**
 * Fetch certificate data for a given project, assets, yields and period.
 *
 * @param projectId - Project/intervention ID
 * @param assetIds - List of asset IDs included in the certificate
 * @param yieldIds - List of yield IDs (kept for validation/future use on platform)
 * @param period - Period string (e.g. "2024-1")
 * @returns CertificateDataResponse or null on error (logged to console)
 */
export async function fetchCertificateData(
  projectId: string,
  assetIds: string[],
  yieldIds: string[],
  period: string,
  orgId: string
): Promise<CertificateDataResponse | null> {
  try {
    const token = getJwtToken();
    if (!token) {
      console.warn("[Certificate Data] No JWT token found in localStorage");
      return null;
    }

    if (!projectId?.trim()) {
      console.error("[Certificate Data] projectId is required");
      return null;
    }
    if (!assetIds?.length) {
      console.error("[Certificate Data] assetIds must be non-empty");
      return null;
    }
    if (!yieldIds?.length) {
      console.error("[Certificate Data] yieldIds must be non-empty");
      return null;
    }
    if (!period?.trim()) {
      console.error("[Certificate Data] period is required");
      return null;
    }
    if (!orgId?.trim()) {
      console.error("[Certificate Data] orgId is required");
      return null;
    }

    const platformUrl = getPlatformUrl();
    const url = new URL(
      "/api/intervention-manager/calculation-results/external/certificate-data",
      platformUrl
    );
    url.searchParams.set("projectId", projectId);
    url.searchParams.set("assetIds", assetIds.join(","));
    url.searchParams.set("yieldIds", yieldIds.join(","));
    url.searchParams.set("period", period);
    url.searchParams.set("orgId", orgId);

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        "[Certificate Data] API error:",
        response.status,
        errorData
      );
      return null;
    }

    const data = (await response.json()) as CertificateDataResponse;
    return data;
  } catch (error) {
    console.error("[Certificate Data] Error fetching certificate data:", error);
    return null;
  }
}

